import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react"

import { Box, useMediaQuery } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { find } from "lodash"
import { useInView } from "react-intersection-observer"

import { gsap, useGSAP } from "@ggs/ui/local/gsap"
import type { FC, ResponsiveImageType } from ".."
import { Container, Grid, IconButton, ResponsiveImage, SvgIcon } from ".."
import { useFetch } from "usehooks-ts"

type ParallaxMediaType = {
  image_1: ResponsiveImageType
  image_2: ResponsiveImageType
  image_3: ResponsiveImageType
  video: string
}

// Override the images default variant with specific picked style.
const resolveDefaultImage = (imageVariants: ResponsiveImageType) => {
  const variantMatch = find(imageVariants?.variants, { style: "3xl" })

  if (variantMatch) {
    return {
      variants: [
        {
          ...variantMatch,
          style: "default",
        },
      ],
    }
  }

  return { variants: [] }
}

const ParallaxMedia: FC<ParallaxMediaType> = (props) => {
  const { image_1: image_raw1, image_2: image_raw2, image_3: image_raw3, video } = props
  const mainRef = useRef<HTMLDivElement>(null)
  const down1Ref = useRef<HTMLDivElement>(null)
  const down2Ref = useRef<HTMLDivElement>(null)
  const up1Ref = useRef<HTMLDivElement>(null)
  const up2Ref = useRef<HTMLDivElement>(null)
  const isImageAvailable = (image: ResponsiveImageType) =>
    Array.isArray(image?.variants) && image?.variants.length > 0

  const image_1 = resolveDefaultImage(image_raw1)
  const image_2 = resolveDefaultImage(image_raw2)
  const image_3 = resolveDefaultImage(image_raw3)

  const isContentValid = Boolean(
    isImageAvailable(image_1) && isImageAvailable(image_2) && isImageAvailable(image_3) && video
  )

  useGSAP(
    () => {
      const mm = gsap.matchMedia()

      // Parallax effect for the images and video (only on desktop)
      mm.add("(min-width: 992px)", () => {
        gsap.from(down1Ref.current, {
          opacity: 0,
          top: 0,
          scrollTrigger: {
            trigger: mainRef.current,
            start: "center 85%",
            end: "center center",
            scrub: true,
          },
        })
        gsap.from(down2Ref.current, {
          opacity: 0,
          top: "-20%",
          scrollTrigger: {
            trigger: mainRef.current,
            start: "center bottom",
            end: "center center",
            scrub: true,
          },
        })

        gsap.from(up1Ref.current, {
          opacity: 0,
          y: 200,
          scrollTrigger: {
            trigger: mainRef.current,
            start: "center 95%",
            end: "center center",
            scrub: true,
          },
        })

        gsap.from(up2Ref.current, {
          opacity: 0,
          bottom: "-15%",
          scrollTrigger: {
            trigger: mainRef.current,
            start: "top center",
            end: "center center",
            scrub: true,
          },
        })
      })
    },
    { scope: mainRef, dependencies: [mainRef, down1Ref, down2Ref, up1Ref, up2Ref] }
  )

  if (!isContentValid) return null

  return (
    <Container sx={{ px: { xs: 0, sm: 5 } }}>
      <Grid>
        <Box
          gridColumn={{ xs: "1 / -1", lg: "2 / -2" }}
          position="relative"
          minHeight={{ xs: 480, lg: 670 }}
          width="100%"
          sx={{ aspectRatio: { xs: "390/480", lg: "1260/670" } }}
          overflow={{ xs: "hidden", lg: "unset" }}
          ref={mainRef}
        >
          <Box
            ref={down1Ref}
            position="absolute"
            borderRadius={1.5}
            left={{ xs: "-10.25%", lg: 0 }}
            top={{ xs: "22.92%", lg: "22.39%" }}
            width={{ xs: "54.1%", lg: 359 }}
            maxWidth="100%"
            overflow="hidden"
            sx={{ aspectRatio: { xs: "211/288", lg: "358/490" } }}
          >
            <ResponsiveImage
              image={image_1}
              width="100%"
              height="100%"
              display="block"
              sx={{ objectFit: "cover" }}
            />
          </Box>
          <Box
            ref={down2Ref}
            position="absolute"
            top={{ xs: "12.08%", lg: 0 }}
            right={{ xs: "-10.25%", lg: 0 }}
            borderRadius={1.5}
            width={{ xs: "63.59%", lg: 359 }}
            maxWidth="100%"
            overflow="hidden"
            sx={{ aspectRatio: { xs: "248/340", lg: "358/490" } }}
          >
            <ResponsiveImage
              image={image_2}
              width="100%"
              height="100%"
              display="block"
              sx={{ objectFit: "cover" }}
            />
          </Box>
          <Box
            ref={up1Ref}
            position="absolute"
            top={{ xs: "auto", lg: 60 }}
            bottom={{ xs: 0, lg: "auto" }}
            left={{ xs: 16, sm: 20, lg: "50%" }}
            right={{ xs: 16, sm: 20, lg: "auto" }}
            borderRadius={1.5}
            width={{ lg: 745 }}
            maxWidth="100%"
            overflow="hidden"
            sx={{
              aspectRatio: { xs: "366/284", lg: "745/490" },
              transform: { lg: "translateX(-50%)" },
            }}
          >
            <ParallaxVideo src={video} />
          </Box>
          <Box
            ref={up2Ref}
            position="absolute"
            top={{ xs: 0, lg: "auto" }}
            bottom={{ xs: "auto", lg: 0 }}
            right={{ xs: "56.41%", lg: 129 }}
            borderRadius={1.5}
            width={{ xs: "25.13%", lg: 229 }}
            maxWidth="100%"
            overflow="hidden"
            sx={{ aspectRatio: "1" }}
          >
            <ResponsiveImage
              image={image_3}
              width="100%"
              height="100%"
              display="block"
              sx={{ objectFit: "cover" }}
            />
          </Box>
        </Box>
      </Grid>
    </Container>
  )
}
export default ParallaxMedia

const ParallaxVideo: FC<{ src: string }> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [isPlaying, setIsPlaying] = useState(false)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const [isVideoVisible, setIsVideoVisible] = useState(false)

  const thumbnail: ResponsiveImageType = useMemo(() => {
    return {
      variants: [
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: 523,
          style: "default",
          title: "",
          url: "https://images.goway.com/production/video/goway-travel-africa-video-cover.jpg",
          width: 450,
        },
      ],
    }
  }, [])

  const playPauseMobile = useCallback (() => {
    if (!isVideoVisible) {
      setIsVideoVisible(true) // Switch to video when play is first clicked on mobile
      setIsPlaying(true)
    }
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }, [isVideoVisible, videoRef.current])


  const playPause = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }, [isVideoVisible, videoRef.current])

  const loadVideo = useCallback(() => {
    setIsVideoLoaded(true)
    if (videoRef.current) {
      videoRef.current.play()
      setIsPlaying(true)
    }
  }, [])

  useEffect(() => {
    if (videoRef.current && inView && !isVideoLoaded && isVideoVisible) {
      loadVideo()
    }
  }, [inView, isVideoLoaded, loadVideo, isVideoVisible])

  // Play the video when it comes into view
  useLayoutEffect(() => {
    if (videoRef.current && inView && !isMobile) {
      videoRef.current.play()
      setIsPlaying(true)
    }
  }, [inView, isMobile])

  return (
    <Box ref={ref}>
      {isMobile && !isVideoVisible ? (
        <Box
          width="100%"
          height="100%"
          sx={{
            aspectRatio: { xs: "366 / 284", lg: "745 / 490" },
            bgcolor: (theme) => theme.palette["secondary.300"].main,
            display: "block",
            objectFit: "cover",
          }}
        >
          <ResponsiveImage
            image={thumbnail}
            width="100%"
            height="100%"
            display="block"
            sx={{ objectFit: "cover" }}
          />
        </Box>
      ) : (
        <Box
          ref={videoRef}
          component="video"
          muted
          loop
          width="100%"
          height="100%"
          sx={{
            aspectRatio: { xs: "366 / 284", lg: "745 / 490" },
            bgcolor: (theme) => theme.palette["secondary.300"].main,
            display: "block",
            objectFit: "cover",
          }}
          playsInline
          preload={isMobile ? "auto" :"none"}
        >
          <source src={src} type="video/mp4" />
        </Box>
      )}
      <IconButton
        icon={
          <SvgIcon
            variant={isPlaying ? "pause" : "play"}
            size={12}
            sx={{ fill: (theme) => theme.palette["neutral.100"].main }}
          />
        }
        onClick={isMobile ? playPauseMobile : playPause}
        sx={(theme) => ({
          "position": "absolute",
          "zIndex": 4,
          "left": theme.spacing(3),
          "bottom": theme.spacing(3),
          "backgroundColor": alpha(theme.palette["neutral.900"].main, 0.05),
          "border": `1px solid ${alpha(theme.palette["neutral.100"].main, 0.2)}`,
          "backdropFilter": "blur(4px)",
          "height": "52px !important",
          "width": "52px !important",

          "&:hover, &:focus-visible": {
            backgroundColor: alpha(theme.palette["neutral.100"].main, 0.4),
          },
        })}
        aria-label="Play Pause button"
      />
    </Box>
  )
}
